import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 pointer-events-auto relative z-10",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 rounded-md",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 rounded-md",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground rounded-md",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 rounded-md",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline",
        // Luxury variants for Faceless Aura Club
        luxury: [
          "relative overflow-hidden rounded-sm",
          "bg-gradient-to-r from-[hsl(var(--gold))] to-[hsl(var(--gold-glow))] text-primary-foreground",
          "font-light tracking-[0.2em] uppercase",
          "border border-[hsl(var(--gold)/0.5)]",
          "shadow-[0_0_30px_hsl(var(--gold)/0.3)]",
          "hover:shadow-[0_0_60px_hsl(var(--gold)/0.5)]",
          "hover:scale-[1.02]",
          "active:scale-[0.98]",
        ].join(" "),
        luxuryOutline: [
          "relative overflow-hidden rounded-sm",
          "bg-transparent text-[hsl(var(--gold))]",
          "font-light tracking-[0.15em] uppercase",
          "border border-[hsl(var(--gold)/0.4)]",
          "hover:bg-[hsl(var(--gold)/0.1)]",
          "hover:border-[hsl(var(--gold)/0.8)]",
          "hover:shadow-[0_0_40px_hsl(var(--gold)/0.2)]",
        ].join(" "),
        hero: [
          "relative overflow-hidden rounded-sm",
          "bg-transparent text-foreground",
          "font-extralight tracking-[0.25em] uppercase text-xs",
          "border border-[hsl(var(--gold)/0.3)]",
          "hover:border-[hsl(var(--gold)/0.6)]",
          "hover:text-[hsl(var(--gold))]",
          "hover:shadow-[0_0_50px_hsl(var(--gold)/0.15)]",
          "transition-all duration-700",
        ].join(" "),
        cta: [
          "relative overflow-hidden group rounded-sm",
          "bg-[hsl(var(--gold))] text-primary-foreground",
          "font-light tracking-[0.2em] uppercase",
          "shadow-[0_0_40px_hsl(var(--gold)/0.4)]",
          "hover:shadow-[0_0_80px_hsl(var(--gold)/0.6)]",
          "hover:scale-[1.03]",
          "active:scale-[0.98]",
          "before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
          "before:-translate-x-full before:hover:translate-x-full before:transition-transform before:duration-1000",
        ].join(" "),
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        xl: "h-14 rounded-sm px-12 text-base",
        xxl: "h-16 rounded-sm px-16 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
